import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axiosInstance from "../../interceptors";
import LoadingState from "../../components/shared/LoadingState";
import ErrorState from "../../components/shared/ErrorState";

// ── /candidate/interviews/:interviewId — feedback report (prompt #2) ──────
// Calls GET /job-posting/candidate-interviews/{interviewId}/feedback which
// aggregates scoring + calls the AI service. The endpoint returns:
//
//   { scoreBand, strengths:[{title,detail}], growthAreas:[{title,detail,tip}], overallTone }
//
// candidateId is taken from the query string when present, else from local
// auth state. The backend requires it for the scoring lookup.

const BAND_TONE = {
  STRONG:     { label: "Strong",     emoji: "✨", color: "bg-emerald-50 border-emerald-200 text-emerald-900" },
  SOLID:      { label: "Solid",      emoji: "👍", color: "bg-vf-tint border-vf-highlight text-vf-base" },
  DEVELOPING: { label: "Developing", emoji: "🌱", color: "bg-amber-50 border-amber-200 text-amber-900" },
};

function bandTone(band) {
  const key = String(band || "").toUpperCase();
  return BAND_TONE[key] || BAND_TONE.DEVELOPING;
}

export default function InterviewFeedback() {
  const { interviewId } = useParams();
  const [params] = useSearchParams();
  const candidateId = useMemo(() => {
    const fromQuery = params.get("candidateId");
    if (fromQuery) return fromQuery;
    return localStorage.getItem("candidateId") || localStorage.getItem("userId") || null;
  }, [params]);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [errorCode, setErrorCode] = useState(null);

  useEffect(() => {
    document.title = "Your feedback - Ample Hire";
  }, []);

  const load = () => {
    if (!interviewId || !candidateId) {
      setLoading(false);
      setError("Missing interview or candidate id");
      setErrorStatus(400);
      return;
    }
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    setErrorCode(null);
    axiosInstance
      .get(`/job-posting/candidate-interviews/${interviewId}/feedback?candidateId=${candidateId}`)
      .then((r) => setReport(r?.data || null))
      .catch((e) => {
        // The axios response interceptor normalizes errors to a flat
        // {status, message, data} shape — not the raw axios shape with
        // e.response.status. Try the flat path first; fall back to the
        // raw shape for any handler that bypasses the interceptor.
        const status = e?.status || e?.response?.status || 500;
        const data = e?.data || e?.response?.data || {};
        setErrorStatus(status);
        setErrorCode(data.code || null);
        // 404 (no scoring yet) and 403/feedback_disabled both render as
        // dedicated friendly panels — not as a generic error.
        if (status === 404 || data.code === "feedback_disabled") return;
        setError(data.message || e?.message || "Couldn't load feedback");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewId, candidateId]);

  // Pending review — score hasn't been computed yet.
  if (!loading && errorStatus === 404) {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <Header />
        <section className="rounded-card bg-white border border-subtle p-6 text-center">
          <div className="text-3xl mb-2" aria-hidden="true">⏳</div>
          <h2 className="text-base font-semibold text-vf-base m-0">Your interview is being reviewed</h2>
          <p className="text-sm text-vf-muted mt-2 mb-0">
            We'll surface your feedback here as soon as the scoring is in. Most candidates see it within 48 hours.
          </p>
        </section>
      </div>
    );
  }

  // Tenant has disabled candidate-facing feedback (V020 toggle).
  if (!loading && errorCode === "feedback_disabled") {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <Header />
        <section className="rounded-card bg-white border border-subtle p-6 text-center">
          <div className="text-3xl mb-2" aria-hidden="true">🔒</div>
          <h2 className="text-base font-semibold text-vf-base m-0">Detailed feedback isn't available for this role</h2>
          <p className="text-sm text-vf-muted mt-2 mb-0">
            This workspace has chosen not to share AI feedback reports with candidates.
            You'll hear back from the hiring team directly about next steps.
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <Header />

      {loading ? (
        <div className="bg-white border border-subtle rounded-card p-4">
          <LoadingState variant="skeleton" shape="card" count={2} />
        </div>
      ) : error ? (
        <ErrorState status={errorStatus} description={error} retry={load} />
      ) : report ? (
        <FeedbackBody report={report} />
      ) : null}
    </div>
  );
}

function Header() {
  return (
    <header>
      <Link
        to="/candidate/interviews"
        className="text-xs text-vf-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
      >
        ← Back to interviews
      </Link>
      <h1 className="text-2xl font-semibold text-vf-base mt-2 mb-1">Your feedback</h1>
      <p className="text-sm text-vf-muted m-0">
        Constructive notes from your interview, designed to help you prepare for what's next.
      </p>
    </header>
  );
}

function FeedbackBody({ report }) {
  const tone = bandTone(report.scoreBand);
  const strengths = Array.isArray(report.strengths) ? report.strengths : [];
  const growth = Array.isArray(report.growthAreas) ? report.growthAreas : [];

  return (
    <>
      {/* Score band — qualitative bucket only, never a raw number */}
      <section
        aria-label="Overall band"
        className={`rounded-card border px-5 py-4 flex items-center gap-4 ${tone.color}`}
      >
        <div className="text-3xl flex-shrink-0" aria-hidden="true">{tone.emoji}</div>
        <div className="flex-1 min-w-0">
          <div className="text-2xs uppercase tracking-wide font-semibold opacity-80">Overall band</div>
          <div className="text-lg font-semibold mt-0.5">{tone.label}</div>
        </div>
      </section>

      {/* Strengths */}
      {strengths.length > 0 && (
        <section aria-label="Strengths" className="rounded-card bg-white border border-subtle p-5">
          <h2 className="text-sm font-semibold text-vf-base m-0 mb-3 flex items-center gap-2">
            <span aria-hidden="true">✓</span> What went well
          </h2>
          <ul className="list-none p-0 m-0 space-y-3">
            {strengths.map((s, i) => (
              <li key={i} className="border-l-2 border-emerald-300 pl-3">
                <div className="text-sm font-semibold text-vf-base">{s.title}</div>
                {s.detail && (
                  <p className="text-sm text-vf-muted mt-1 m-0">{s.detail}</p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Growth areas */}
      {growth.length > 0 && (
        <section aria-label="Growth areas" className="rounded-card bg-white border border-subtle p-5">
          <h2 className="text-sm font-semibold text-vf-base m-0 mb-3 flex items-center gap-2">
            <span aria-hidden="true">→</span> For next time
          </h2>
          <ul className="list-none p-0 m-0 space-y-4">
            {growth.map((g, i) => (
              <li key={i} className="border-l-2 border-vf-primary/40 pl-3">
                <div className="text-sm font-semibold text-vf-base">{g.title}</div>
                {g.detail && (
                  <p className="text-sm text-vf-muted mt-1 mb-2 m-0">{g.detail}</p>
                )}
                {g.tip && (
                  <div className="text-xs bg-vf-tint border border-vf-highlight rounded px-3 py-2 text-vf-base">
                    <span className="font-semibold">Tip:</span> {g.tip}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Privacy note */}
      <section className="text-xs text-vf-muted bg-vf-bg border border-subtle rounded-card px-4 py-3" aria-label="Privacy note">
        <span className="font-semibold text-vf-base">Your feedback is private.</span>{" "}
        The hiring team sees a separate detailed scorecard — what's above is written
        just for you.
      </section>
    </>
  );
}
